import React from 'react';
import { Box, Paper, useTheme } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { addMarketWatchInstrumentAction } from '../../../store/actions/watchlist';
import ExpandedViewHeader from './ExpandedViewHeader';
import MarketWatchExpandedTable from './MarketWatchExpandedTable';
import useExpandedWatchlist from './useExpandedWatchlist';

// Master list of available columns
const ALL_COLUMNS = [
  { field: 'symbol', headerName: 'Symbol', align: 'left', minWidth: '160px' },
  { field: 'ltp', headerName: 'LTP', align: 'right', minWidth: '80px' },
  { field: 'absoluteChange', headerName: 'Change', align: 'right', minWidth: '80px' },
  { field: 'percentChange', headerName: '%Chg', align: 'right', minWidth: '70px' },
  { field: 'volume', headerName: 'Volume', align: 'right', minWidth: '90px' },
  { field: 'openPrice', headerName: 'Open', align: 'right', minWidth: '80px' },
  { field: 'highPrice', headerName: 'Day High', align: 'right', minWidth: '80px' },
  { field: 'lowPrice', headerName: 'Day Low', align: 'right', minWidth: '80px' },
  { field: 'closePrice', headerName: 'Prev Close', align: 'right', minWidth: '80px' },
  { field: 'bestBidPrice', headerName: 'Best Bid', align: 'right', minWidth: '80px' },
  { field: 'bestBidQuantity', headerName: 'Bid Qty', align: 'right', minWidth: '80px' },
  { field: 'bestAskPrice', headerName: 'Best Ask', align: 'right', minWidth: '80px' },
  { field: 'bestAskQuantity', headerName: 'Ask Qty', align: 'right', minWidth: '80px' },
  { field: 'lastTradedTime', headerName: 'LTT', align: 'right', minWidth: '90px' },
];

const MarketWatchExpandedView = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { watchlists, watchListIndex } = useSelector((state) => state.marketwatch);
  const currentInstruments = watchlists[watchListIndex]?.instruments || [];

  const addInstrument = async (instrument) => {
    // Check if instrument already exists in current watchlist
    let idx = watchlists[watchListIndex].instruments.findIndex(
      (instr) => instr.token === instrument.token && instr.exchange === instrument.exchange
    );
    
    // If instrument already exists, notify and return
    if (idx !== -1) {
      enqueueSnackbar(`${instrument.display_name || instrument.trading_symbol} is already in your watchlist`, {
        variant: "info",
        autoHideDuration: 2000
      });
      return;
    }

    // Check if watchlist is full
    if (watchlists[watchListIndex].instruments.length >= 20) {
      enqueueSnackbar("Switch to another watchlist to add more instruments", {
        variant: "info",
      });
      return;
    }

    // Prepare payload exactly like collapsed view
    let payload = {
      mw_id: watchlists[watchListIndex].id,
      exchange: instrument.exchange,
      trading_symbol: instrument.trading_symbol,
      token: instrument.token,
      execution: instrument.execution === "SPREAD" ? "SPREAD" : "NORMAL",
    };

    const { message, status } = await dispatch(
      addMarketWatchInstrumentAction(watchListIndex, payload)
    );

    if (status === "success") {
      enqueueSnackbar(message, { variant: "success", autoHideDuration: 1000 });
    } else {
      enqueueSnackbar(message || "Error adding instrument", { variant: "error", autoHideDuration: 1000 });
    }
  };

  const {
    tableColumns,
    allColumns,
    hiddenColumns,
    toggleColumn,
    reorderColumns,
    resetSettings
  } = useExpandedWatchlist(ALL_COLUMNS);

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden',
      backgroundColor: 'background.default'
    }}>
      <Box sx={{ position: 'relative', zIndex: 10 }}>
        <ExpandedViewHeader 
          watchlists={watchlists} 
          watchListIndex={watchListIndex} 
          addInstrument={addInstrument}
          columnSettings={{
            allColumns,
            hiddenColumns,
            onToggle: toggleColumn,
            onReset: resetSettings,
            onReorder: reorderColumns
          }}
        />
      </Box>
      
      <Box sx={{ 
        flex: 1, 
        m: { xs: 1, md: 2 }, 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0,
        position: 'relative',
        zIndex: 1
      }}>
        <Paper 
          elevation={0}
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            borderRadius: 2, 
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <MarketWatchExpandedTable 
            instruments={currentInstruments} 
            columns={tableColumns} 
            watchListIndex={watchListIndex}
            watchlists={watchlists}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default MarketWatchExpandedView;
